// kernel/inventoryQueryCache.ts
//
// The one place where a realtime/mutation event decides what happens to the query cache of an
// inventory slice. A signature encodes server-side filters, ordering and total, so the client can
// never insert a freshly created entity into a cached list: it does not know whether the entity
// belongs to it, at which position, nor what `total` becomes. StudioLayer UPDATE events also expire
// signatures because detachment changes workspace-filtered membership.
//
// This module imports types only, so it stays importable outside the Kernel import graph (and
// therefore testable).
import type { InventoryEntityType, LeasingEventType, QueryCacheEntry } from '#types/types';

export type InventoryQueryCache = Record<string, QueryCacheEntry>;

// Types that stream in bulk. A scoring run emits hundreds of CREATE in a row; expiring the cache on
// each of them would re-query the shortlist out from under the user. They already surface in a
// mounted list through the ghost upsert, so they opt out of the create rule.
export const UPSERT_ON_GHOST_UPDATE_TYPES = new Set<InventoryEntityType>([
  'Lead',
  'SourcingCampaign',
  'Notification',
]);

export const applyEventToQueryCache = (
  previous: InventoryQueryCache | undefined,
  event: { entityType: InventoryEntityType; entityId: string; eventType: LeasingEventType },
): { queryCache: InventoryQueryCache; requeryLists: boolean } => {
  const queryCache: InventoryQueryCache = { ...(previous || {}) };

  if (event.eventType === 'DELETE') {
    // The removed id is known, so the cached lists are corrected in place instead of expired.
    Object.keys(queryCache).forEach((signature) => {
      const entry = queryCache[signature];
      if (!entry || !Array.isArray(entry.ids) || !entry.ids.includes(event.entityId)) return;

      const nextIds = entry.ids.filter((id: string) => id !== event.entityId);
      const removedCount = entry.ids.length - nextIds.length;
      const nextTotal = Math.max(0, (typeof entry.total === 'number' ? entry.total : entry.ids.length) - removedCount);
      queryCache[signature] = { ...entry, ids: nextIds, total: nextTotal };
    });
    return { queryCache, requeryLists: false };
  }

  if (event.eventType !== 'CREATE' && event.entityType !== 'StudioLayer') {
    return { queryCache, requeryLists: false };
  }
  if (event.eventType === 'CREATE' && UPSERT_ON_GHOST_UPDATE_TYPES.has(event.entityType)) {
    return { queryCache, requeryLists: false };
  }

  Object.keys(queryCache).forEach((signature) => {
    const entry = queryCache[signature];
    if (!entry || entry.timestamp === 0) return;
    queryCache[signature] = { ...entry, timestamp: 0 };
  });
  return { queryCache, requeryLists: true };
};
